import { motion } from "framer-motion";
import { useRef } from "react";
import FileIcon from "../../../../public/images/FileIcon";
import ReloadIcon from "../../../../public/images/ReloadIcon";
import UploadFile from "../../../../public/images/UploadFile";
import { applyOneLineWidthLimit } from "./chatFlow";

export default function ChatMessage({
  m,
  botVariant,
  userVariant,
  sceneActive,
  editingId,
  editingText,
  setEditingText,

  verificationMethod,
  communicationPlatform,
  useSameAccountChoice,
  selectedNumbersMode,

  step,
  startEdit,
  saveEdit,

  selectVerificationOption,
  selectPlatformOption,
  selectSameAccountOption,
  selectNumbersOption,
  uploadedDoc,
  uploading,
  uploadDocument,
}) {
  const isEditing = editingId === m.id;

  const onEditKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveEdit();
    }
  };
  const fileRef = useRef(null);

  const pickFile = () => {
    const allowEditAfterUpload = !!uploadedDoc;

    const blocked =
      sceneActive ||
      editingId ||
      uploading ||
      (!allowEditAfterUpload && step !== m.stepIndex);

    if (blocked) return;

    fileRef.current?.click();
  };

  const formatBytes = (bytes) => {
    const b = Number(bytes || 0);
    if (!b) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.min(Math.floor(Math.log(b) / Math.log(k)), sizes.length - 1);
    return `${(b / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
  };

  const editAllowed = !!uploadedDoc || step === m.stepIndex;
  const isDisabled = sceneActive || editingId || uploading || !editAllowed;
  return (
    <motion.div
      className={[
        "chat__sms__messages__bubble",
        `chat__sms__messages__bubble--${m.from}`,
        isEditing ? "chat__sms__messages__bubble--editing" : "",
      ].join(" ")}
      variants={m.from === "bot" ? botVariant : userVariant}
      initial="hidden"
      animate="visible"
      exit="hidden"
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {m.from === "bot" && (
        <>
          <img src="/images/miniLogo.svg" alt="Bot" />

          {m.type === "text" && <span>{m.text}</span>}

          {m.type === "rich" && m.textParts && (
            <span>
              {m.textParts.before}
              <div
                className={[
                  "chat__sms__messages__bubble__chat__platformWord",
                  `chat__sms__messages__bubble__chat__platformWord--${m.textParts.platformKey}`,
                ].join(" ")}
              >
                {m.textParts.word}
              </div>
              {m.textParts.after}
            </span>
          )}

          {m.type === "options" && (
            <div className="chat__sms__messages__bubble__options">
              <div className="chat__sms__messages__bubble__options__title">
                {m.text}
              </div>

              <div className="chat__sms__messages__bubble__options__list">
                {m.options?.map((opt) => {
                  const isVerifStep = m.stepIndex === 1;
                  const isCommPlatformStep = m.stepIndex === 5;
                  const isSameAccountStep = m.stepIndex === 6;
                  const isNumbersStep = m.stepIndex === 9;

                  const checked = isVerifStep
                    ? verificationMethod === opt
                    : isCommPlatformStep
                      ? communicationPlatform === opt
                      : isSameAccountStep
                        ? useSameAccountChoice === opt
                        : isNumbersStep
                          ? selectedNumbersMode === opt
                          : false;

                  const disabled =
                    sceneActive || editingId || step !== m.stepIndex;

                  const onPick = () => {
                    if (isVerifStep) selectVerificationOption(opt);
                    if (isCommPlatformStep) selectPlatformOption(opt);
                    if (isSameAccountStep) selectSameAccountOption(opt);
                    if (isNumbersStep) selectNumbersOption(opt);
                  };

                  return (
                    <label
                      key={opt}
                      className={[
                        "chat__sms__messages__bubble__options__list__item",
                        "chat__sms__messages__bubble__options__list__item__radio",
                        disabled ? "isDisabled" : "",
                      ].join(" ")}
                    >
                      <input
                        type="radio"
                        name={`options-step-${m.stepIndex}`}
                        value={opt}
                        checked={checked}
                        disabled={disabled}
                        onChange={onPick}
                      />
                      <div className="chat__sms__messages__bubble__options__list__item__radio__control" />
                      <span className="chat__sms__messages__bubble__options__list__item__radio__label">
                        {opt}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          {m.type === "upload" && (
            <div className="chat__sms__messages__bubble__upload">
              <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                style={{ display: "none" }}
                disabled={
                  sceneActive ||
                  editingId ||
                  uploading ||
                  (!uploadedDoc && step !== m.stepIndex)
                }
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  uploadDocument?.(file);
                  e.target.value = "";
                }}
              />
              {m.showIcon && (
                <div
                  className="chat__sms__messages__bubble__upload__icon"
                  onClick={pickFile}
                  role="button"
                  tabIndex={0}
                >
                  {uploadedDoc ? <FileIcon /> : <UploadFile />}
                </div>
              )}
              {!uploadedDoc ? (
                <div className="chat__sms__messages__bubble__upload__title">
                  {m.text}
                </div>
              ) : (
                <div className="chat__sms__messages__bubble__upload__file">
                  <div className="chat__sms__messages__bubble__upload__fileName">
                    {uploadedDoc.name}
                  </div>
                  <div className="chat__sms__messages__bubble__upload__fileSize">
                    {formatBytes(uploadedDoc.size)}
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={pickFile}
                className={[
                  "chat__sms__messages__bubble__upload__btn",
                  isDisabled ? "isDisabled" : "",
                ].join(" ")}
                disabled={isDisabled}
              >
                {uploading ? (
                  "Uploading..."
                ) : uploadedDoc ? (
                  <ReloadIcon />
                ) : (
                  m.buttonText || "Upload"
                )}
              </button>
            </div>
          )}
        </>
      )}

      {m.from === "user" && (
        <>
          {!isEditing ? (
            <div className="chat__sms__messages__bubble__content">
              {m.invalid && (
                <div className="chat__sms__messages__bubble__content__errorTop">
                  {m.error}
                </div>
              )}

              <span
                className={[
                  "chat__sms__messages__bubble__content__text",
                  m.invalid ? "is-invalid" : "",
                ].join(" ")}
              >
                <button
                  type="button"
                  onClick={() => startEdit(m)}
                  aria-label="Edit message"
                >
                  <img src="/images/edit.svg" alt="" />
                </button>
                {m.text}
              </span>
            </div>
          ) : (
            <div className="chat__sms__messages__bubble__editPanel">
              <textarea
                value={editingText}
                rows={1}
                onKeyDown={onEditKeyDown}
                onChange={(e) => {
                  const el = e.target;
                  const prev = editingText;
                  applyOneLineWidthLimit(
                    e.target.value,
                    el,
                    prev,
                    setEditingText,
                  );
                }}
              />
              <button
                type="button"
                onClick={saveEdit}
                className="chat__sms__messages__bubble__editPanel__saveBtn"
              >
                Save
              </button>
            </div>
          )}

          <img src="/images/userIcon.svg" alt="You" />
        </>
      )}
    </motion.div>
  );
}
